import { Frequency } from '../../../../types/event';

function getWeekdayTranslationKey(day: number): string {
  switch (day) {
    case 0:
      return 'general.daysOfWeek.sunday';
    case 1:
      return 'general.daysOfWeek.monday';
    case 2:
      return 'general.daysOfWeek.tuesday';
    case 3:
      return 'general.daysOfWeek.wednesday';
    case 4:
      return 'general.daysOfWeek.thursday';
    case 5:
      return 'general.daysOfWeek.friday';
    case 6:
      return 'general.daysOfWeek.saturday';
    default:
      return 'general.daysOfWeek.monday';
  }
}

function formatTimeForRecurrence(date: Date, language: string): string {
  return new Intl.DateTimeFormat(language, {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).format(date);
}

export function getRecurrenceSummary(params: {
  frequency?: Frequency;
  startTime?: Date;
  language: string;
  t: (key: string, options?: Record<string, unknown>) => string;
}): string {
  const { frequency, startTime, language, t } = params;

  if (!startTime) {
    return t('page.event.schedules.recurrence.summaryMissingStart');
  }

  if (!frequency) {
    return t('page.event.schedules.recurrence.summaryMissingFrequency');
  }

  const time = formatTimeForRecurrence(startTime, language);
  const dayOfWeek = t(getWeekdayTranslationKey(startTime.getDay()));
  const dayOfMonth = startTime.getDate();
  const monthName = new Intl.DateTimeFormat(language, { month: 'long' }).format(startTime);

  switch (frequency) {
    case Frequency.DAILY:
      return t('page.event.schedules.recurrence.summaryDaily', { time });
    case Frequency.WEEKLY:
      return t('page.event.schedules.recurrence.summaryWeekly', { dayOfWeek, time });
    case Frequency.BI_WEEKLY:
      return t('page.event.schedules.recurrence.summaryBiWeekly', { dayOfWeek, time });
    case Frequency.MONTHLY:
      return t('page.event.schedules.recurrence.summaryMonthly', { dayOfMonth, time });
    case Frequency.YEARLY:
      return t('page.event.schedules.recurrence.summaryYearly', {
        dayOfMonth,
        monthName,
        time,
      });
    default:
      return t('page.event.schedules.recurrence.summaryMissingFrequency');
  }
}

export function getDefaultRecurrenceEndDate(frequency: Frequency, startTime?: Date): Date {
  const endDate = startTime ? new Date(startTime) : new Date();

  switch (frequency) {
    case Frequency.DAILY:
      endDate.setDate(endDate.getDate() + 7);
      break;
    case Frequency.WEEKLY:
      endDate.setMonth(endDate.getMonth() + 1);
      break;
    case Frequency.BI_WEEKLY:
      endDate.setMonth(endDate.getMonth() + 2);
      break;
    case Frequency.MONTHLY:
      endDate.setFullYear(endDate.getFullYear() + 1);
      break;
    case Frequency.YEARLY:
      endDate.setFullYear(endDate.getFullYear() + 5);
      break;
    default:
      throw new Error('Unsupported frequency');
  }

  return endDate;
}

export function parseShiftCapacity(assigneeCount: string): number | undefined {
  if (assigneeCount.trim() === '') {
    return undefined;
  }

  const parsed = Number(assigneeCount);
  if (!Number.isInteger(parsed) || parsed < 1) {
    return undefined;
  }

  return parsed;
}
